import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import templateService from "../../../services/template.service";
import Loading from "../../common/Loading";
import Swal from '../../helpers/swal';
import EmailEditor from "../../helpers/email-editor/EmailEditor";


const EditClinicEmailTemplate = () => {
  const navigate = useNavigate();
  const saveFnRef = useRef(null);
  const [jsonData, setJsonData] = useState(null);
  const { clinicId, id } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    reminderName: "",
    content: "",
    subjectLine: "",
  });
  const [originalData, setOriginalData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if ID is valid
        if (!id || id === "undefined" || !clinicId || clinicId === "undefined") {
          throw new Error("Invalid template ID or clinic ID");
        }

        const response = await templateService.getEmailTemplateById(clinicId, id);

        // Check if response and data exist
        if (!response || !response.data) {
          throw new Error("No template data received");
        }

        const template = response.data;

        // Handle different response structures
        const templateData = template.data || template; // Handle both {data: {...}} and direct object

        setFormData({
          reminderName: templateData.reminderName || "",
          content: templateData.content || "",
          subjectLine: templateData.subjectLine || "",
        });
        
        // Parse jsonContent if it's a string
        try {
          const parsedJson = typeof templateData.jsonContent === 'string' 
            ? JSON.parse(templateData.jsonContent) 
            : templateData.jsonContent;
          setJsonData(parsedJson);
        } catch (parseError) {
          console.error("Error parsing jsonContent:", parseError);
          setJsonData(null);
        }
        
        setOriginalData(templateData);
      } catch (error) {
        console.error("Error fetching template:", error);
        setError(error.message);

        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Failed to load template. Please try again.",
          confirmButtonColor: "#dc3545",
        }).then(() => {
          navigate('/clinic-templates');
        });
      } finally {
        setLoading(false);
      }
    };

    if (id && clinicId) {
      fetchTemplate();
    } else {
      setLoading(false);
      setError("No template ID or clinic ID provided");
    }
  }, [id, clinicId, navigate]);

  const handleEmailDesignSave = async () => {
    if (saveFnRef.current) {
      try {
        const { htmltemplate, template, thumbnail } = await saveFnRef.current();
        console.log("htmltemplate", htmltemplate);
        console.log("template", template);
        console.log("thumbnail", thumbnail);
        return { htmltemplate, template, thumbnail };
      } catch (err) {
        console.error('Save failed:', err);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      const result = await handleEmailDesignSave();
      if (!result) throw new Error('Could not get email design');

      const { htmltemplate, template } = result;
      const requestBody = {
        reminderName: formData.reminderName,
        subjectLine: formData.subjectLine,
        content: htmltemplate,
        jsonContent: template,
        reminderTypeId: originalData.reminderTypeId,
        status: originalData.status,
      };

      await templateService.updateEmailTemplate(clinicId, id, requestBody);

      await Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Email template updated successfully!",
        confirmButtonColor: "#198754",
        timer: 2000,
        showConfirmButton: true,
      });
    } catch (error) {
      console.error("Error updating Email template:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to update Email template. Please try again.";

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    const hasChanges =
      formData.content !== originalData?.content ||
      formData.subjectLine !== originalData?.subjectLine;

    if (hasChanges) {
      Swal.fire({
        title: "Unsaved Changes",
        text: "You have unsaved changes. Are you sure you want to leave?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#198754",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, discard changes",
        cancelButtonText: "No, keep editing",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/clinic-templates');
        }
      });
    } else {
      navigate('/clinic-templates');
    }
  };

  if (loading) {
    return (
      <div className="container container-xxl">
        <div className="pagetitle">
          <h1>
            <i className="bi bi-chat-dots text-success me-2"></i> Edit Email
            Template
          </h1>
        </div>
        <Loading />
      </div>
    );
  }

  if (error || !originalData) {
    return (
      <div className="container container-xxl">
        <div className="pagetitle">
          <h1>
            <i className="bi bi-chat-dots text-success me-2"></i> Edit Email
            Template
          </h1>
        </div>
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error || "Template not found or failed to load."}
          <div className="mt-2">
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => navigate('/clinic-templates')}
            >
              <i className="bi bi-arrow-left me-1"></i>
              Back to Templates
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container container-xxl">
      <div className="pagetitle d-flex justify-content-between align-items-center">
        <h1>
          <i className="bi bi-chat-dots text-success me-2"></i> Edit Email
          Template
        </h1>
      </div>

      <div className="card card-padding">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* Read-only Template Information */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="mb-2">
                  <label className="form-label fw-semibold">
                    Template Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.reminderName}
                    readOnly
                    disabled
                  />
                  <div className="form-text">
                    Template name cannot be changed
                  </div>
                </div>
                <div className="mb-2">
                  <label className="form-label fw-semibold">Email Subject *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.subjectLine}
                    onChange={(e) => setFormData({...formData, subjectLine: e.target.value})}
                    placeholder="Enter email subject line"
                    required
                  />
                </div>
              </div>
              <div className="col-md-6">
                {/* Template Metadata */}
                <div className="alert alert-info mb-4">
                  <div className="d-flex flex-column">
                    <div>
                      <span className="fw-bold">Template ID: </span>
                      <span className="small font-monospace">
                        {originalData.id}
                      </span>
                    </div>
                    <div>
                      <span className="fw-bold">Clinic ID: </span>
                      <span className="small font-monospace">
                        {clinicId}
                      </span>
                    </div>
                    <div>
                      <span className="fw-bold">Created: </span>
                      <span className="small">
                        {originalData.createdAt || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="fw-bold">Last Modified: </span>
                      <span className="small">
                        {originalData.modifiedAt || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex gap-2 justify-content-end align-items-center mb-3">
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={handleCancel}
                disabled={submitting}
              >
                <i className="bi bi-x me-1"></i> Cancel
              </button>
              <button
                type="submit"
                className="btn btn-success btn-sm"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-1"></i> Update Template
                  </>
                )}
              </button>
            </div>

            <div>
              {jsonData ? (
                <EmailEditor
                  key={`email-editor-${id}`}
                  loadjson={jsonData}
                  emailSubject={formData.subjectLine}
                  saveemail={(saveFn) => (saveFnRef.current = saveFn)}
                />
              ) : (
                <div className="d-flex justify-content-center align-items-center" style={{minHeight: "400px"}}>
                  <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                      <span className="visually-hidden">Loading editor...</span>
                    </div>
                    <p className="text-muted">Loading email editor...</p>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditClinicEmailTemplate;